import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Pagination from "@mui/material/Pagination";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import moment from "moment";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { GetAdminCopilotMaterialCall } from "../../redux/api/lectureLab.api";

const SOURCE_TYPE_LABEL = {
  pdf: "PDF",
  ppt: "PowerPoint",
  image: "Image",
  audio: "Audio",
};

const STATUS_LABEL = {
  queued: "Queued",
  processing: "Processing",
  ready: "Ready",
  failed: "Failed",
};

const PER_PAGE_OPTIONS = [5, 10, 25, 50];

function statusChipColor(status) {
  switch (status) {
    case "ready":
      return "success";
    case "processing":
      return "info";
    case "queued":
      return "warning";
    case "failed":
      return "error";
    default:
      return "default";
  }
}

function formatDateTime(value) {
  if (!value) return "—";
  return moment(value).format("YYYY-MM-DD HH:mm");
}

function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function initialsFor(user) {
  if (!user) return "?";
  const first = user.firstName?.charAt(0) || "";
  const last = user.lastName?.charAt(0) || "";
  const combo = `${first}${last}`.toUpperCase();
  if (combo) return combo;
  if (user.email) return user.email.charAt(0).toUpperCase();
  return "?";
}

/*
  Mirror the same `normalizeKeyStructure` shape as the mobile app:
  legacy study packs may have plain strings for key structures, the new
  ones have `{ title, description }` objects. We accept both so old
  uploads still render with a heading + body.
*/
function normalizeKeyStructure(entry) {
  if (entry == null) return null;
  if (typeof entry === "string") {
    const raw = entry.trim();
    if (!raw) return null;
    return { title: raw, description: "" };
  }
  if (typeof entry === "object") {
    const title = String(
      entry.title ?? entry.name ?? entry.structure ?? entry.term ?? ""
    ).trim();
    const description = String(
      entry.description ??
        entry.explanation ??
        entry.detail ??
        entry.note ??
        ""
    ).trim();
    if (!title && !description) return null;
    return { title: title || description, description: title ? description : "" };
  }
  return null;
}

/*
  Generic pagination helper used by every tab body. Keeps the tab
  contract identical and lets us swap "page" controls without touching
  the data shapes. Returns the visible slice + a footer node.
*/
function usePagedSlice(items, perPage) {
  const [page, setPage] = useState(1);

  useEffect(() => {
    // Reset to page 1 whenever the source list changes or the per-page
    // size changes so we never sit on a now-empty page.
    setPage(1);
  }, [items, perPage]);

  const totalPages = Math.max(Math.ceil(items.length / perPage), 1);
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * perPage;
  const slice = items.slice(start, start + perPage);

  return { slice, page: safePage, totalPages, setPage };
}

function PaginationFooter({ page, totalPages, onPageChange, total, label }) {
  if (total === 0) return null;
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", sm: "center" }}
      justifyContent="space-between"
      sx={{ pt: 2 }}
    >
      <Typography variant="caption" color="text.secondary">
        Showing {label} · {total} total
      </Typography>
      {totalPages > 1 ? (
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_e, p) => onPageChange(p)}
          size="small"
          color="primary"
          showFirstButton
          showLastButton
        />
      ) : null}
    </Stack>
  );
}

function EmptyState({ message }) {
  return (
    <Box sx={{ py: 6, textAlign: "center" }}>
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );
}

function SummaryTab({ studyPack }) {
  if (!studyPack?.summary?.trim()) {
    return <EmptyState message="No summary produced for this material." />;
  }

  const highYield = Array.isArray(studyPack.highYieldPoints)
    ? studyPack.highYieldPoints.filter(Boolean)
    : [];
  const practical = Array.isArray(studyPack.practicalQuestions)
    ? studyPack.practicalQuestions.filter(Boolean)
    : [];

  return (
    <Stack spacing={3}>
      <Box>
        <MDTypography
          variant="button"
          color="info"
          fontWeight="bold"
          sx={{ mb: 1, display: "block", letterSpacing: "0.04em" }}
        >
          SUMMARY
        </MDTypography>
        <Typography
          variant="body1"
          sx={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            lineHeight: 1.7,
          }}
        >
          {studyPack.summary}
        </Typography>
      </Box>

      {highYield.length > 0 ? (
        <Box>
          <MDTypography
            variant="button"
            color="info"
            fontWeight="bold"
            sx={{ mb: 1, display: "block", letterSpacing: "0.04em" }}
          >
            HIGH-YIELD POINTS
          </MDTypography>
          <Stack component="ul" spacing={1} sx={{ pl: 3, m: 0 }}>
            {highYield.map((point, idx) => (
              <Box component="li" key={idx}>
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  {point}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      ) : null}

      {practical.length > 0 ? (
        <Box>
          <MDTypography
            variant="button"
            color="info"
            fontWeight="bold"
            sx={{ mb: 1, display: "block", letterSpacing: "0.04em" }}
          >
            PRACTICAL QUESTIONS
          </MDTypography>
          <Stack component="ul" spacing={1} sx={{ pl: 3, m: 0 }}>
            {practical.map((q, idx) => (
              <Box component="li" key={idx}>
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  {q}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      ) : null}
    </Stack>
  );
}

function StructuresTab({ studyPack, perPage }) {
  const items = useMemo(() => {
    const raw = Array.isArray(studyPack?.keyStructures)
      ? studyPack.keyStructures
      : [];
    return raw.map(normalizeKeyStructure).filter(Boolean);
  }, [studyPack?.keyStructures]);

  const { slice, page, totalPages, setPage } = usePagedSlice(items, perPage);

  if (!items.length) {
    return <EmptyState message="No key structures generated for this material." />;
  }

  const start = (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, items.length);

  return (
    <Box>
      <Stack spacing={1.5}>
        {slice.map((item, idx) => {
          const globalIdx = (page - 1) * perPage + idx + 1;
          return (
            <Paper
              key={`${item.title || "structure"}-${idx}`}
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                borderLeft: "4px solid",
                borderLeftColor: "primary.main",
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Chip
                  label={String(globalIdx).padStart(2, "0")}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ wordBreak: "break-word", lineHeight: 1.3 }}
                  >
                    {item.title}
                  </Typography>
                  {item.description ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 0.75,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        lineHeight: 1.6,
                      }}
                    >
                      {item.description}
                    </Typography>
                  ) : null}
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
      <PaginationFooter
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        total={items.length}
        label={`${start}–${end}`}
      />
    </Box>
  );
}

function ImagesTab({ studyPack, perPage }) {
  /*
    Merge the same two sources the mobile app does — `sections` entries
    of type "image" and the `embeddedImages` list — and dedupe by URL so
    the admin sees one card per figure even if it appears in both lists.
  */
  const items = useMemo(() => {
    if (!studyPack) return [];
    const seen = new Set();
    const out = [];
    const push = (imageUrl, caption, key) => {
      if (!imageUrl || seen.has(imageUrl)) return;
      seen.add(imageUrl);
      out.push({ imageUrl, caption: caption || "", key: key || imageUrl });
    };
    (studyPack.sections || []).forEach((s, idx) => {
      if (s?.type === "image") push(s.imageUrl, s.caption, `section-${idx}`);
    });
    (studyPack.embeddedImages || []).forEach((img, idx) => {
      push(img?.imageUrl, img?.caption, `embedded-${idx}`);
    });
    return out;
  }, [studyPack]);

  const { slice, page, totalPages, setPage } = usePagedSlice(items, perPage);

  if (!items.length) {
    return <EmptyState message="No figures extracted from this material." />;
  }

  const start = (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, items.length);

  return (
    <Box>
      <Grid container spacing={2}>
        {slice.map((item) => {
          const captionParts = (item.caption || "").split("\n\n");
          const captionTitle = captionParts[0] || "";
          const captionBody = captionParts.slice(1).join("\n\n");
          return (
            <Grid item xs={12} md={6} key={item.key}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <Box
                  component="img"
                  src={item.imageUrl}
                  alt={captionTitle || "Lecture figure"}
                  sx={{
                    width: "100%",
                    maxHeight: 360,
                    objectFit: "contain",
                    bgcolor: "grey.900",
                  }}
                />
                {item.caption ? (
                  <Box sx={{ p: 2 }}>
                    {captionBody ? (
                      <>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{ wordBreak: "break-word", lineHeight: 1.3 }}
                        >
                          {captionTitle}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 0.75,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            lineHeight: 1.6,
                          }}
                        >
                          {captionBody}
                        </Typography>
                      </>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          lineHeight: 1.6,
                        }}
                      >
                        {captionTitle}
                      </Typography>
                    )}
                  </Box>
                ) : null}
              </Paper>
            </Grid>
          );
        })}
      </Grid>
      <PaginationFooter
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        total={items.length}
        label={`${start}–${end}`}
      />
    </Box>
  );
}

function QuizTab({ studyPack, perPage }) {
  const questions = useMemo(
    () =>
      Array.isArray(studyPack?.quiz?.questions)
        ? studyPack.quiz.questions
        : [],
    [studyPack?.quiz?.questions]
  );

  const { slice, page, totalPages, setPage } = usePagedSlice(
    questions,
    perPage
  );

  if (!questions.length) {
    return <EmptyState message="No quiz questions generated for this material." />;
  }

  const start = (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, questions.length);

  return (
    <Box>
      {studyPack?.quiz?.title ? (
        <MDTypography
          variant="button"
          color="info"
          fontWeight="bold"
          sx={{ mb: 1.5, display: "block", letterSpacing: "0.04em" }}
        >
          {studyPack.quiz.title}
        </MDTypography>
      ) : null}
      <Stack spacing={2}>
        {slice.map((q, idx) => {
          const globalIdx = (page - 1) * perPage + idx + 1;
          const correctIdx = Number.isInteger(q?.correctIndex)
            ? q.correctIndex
            : -1;
          const choices = Array.isArray(q?.choices) ? q.choices : [];
          return (
            <Paper
              key={q?.id || globalIdx}
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Chip
                  label={`Q${globalIdx}`}
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 700 }}
                />
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{
                    flex: 1,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    lineHeight: 1.5,
                  }}
                >
                  {q?.prompt || "—"}
                </Typography>
              </Stack>
              <Stack spacing={1} sx={{ mt: 2 }}>
                {choices.map((choice, ci) => {
                  const isCorrect = ci === correctIdx;
                  return (
                    <Stack
                      key={ci}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                      sx={{
                        p: 1.25,
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: isCorrect ? "success.light" : "divider",
                        bgcolor: (theme) =>
                          isCorrect
                            ? theme.palette.mode === "dark"
                              ? "rgba(76, 175, 80, 0.14)"
                              : "rgba(76, 175, 80, 0.08)"
                            : "transparent",
                      }}
                    >
                      {isCorrect ? (
                        <CheckCircleIcon
                          fontSize="small"
                          color="success"
                          sx={{ mt: "2px" }}
                        />
                      ) : (
                        <HighlightOffIcon
                          fontSize="small"
                          sx={{ color: "text.disabled", mt: "2px" }}
                        />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          wordBreak: "break-word",
                          fontWeight: isCorrect ? 600 : 400,
                          color: isCorrect ? "success.dark" : "text.primary",
                        }}
                      >
                        {String.fromCharCode(65 + ci)}. {choice}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Paper>
          );
        })}
      </Stack>
      <PaginationFooter
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        total={questions.length}
        label={`${start}–${end}`}
      />
    </Box>
  );
}

function FlashcardsTab({ studyPack, perPage }) {
  const cards = useMemo(
    () =>
      Array.isArray(studyPack?.flashcards) ? studyPack.flashcards : [],
    [studyPack?.flashcards]
  );

  const { slice, page, totalPages, setPage } = usePagedSlice(cards, perPage);

  if (!cards.length) {
    return <EmptyState message="No flashcards generated for this material." />;
  }

  const start = (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, cards.length);

  return (
    <Box>
      <Grid container spacing={2}>
        {slice.map((card, idx) => {
          const globalIdx = (page - 1) * perPage + idx + 1;
          return (
            <Grid item xs={12} md={6} key={card?.id || globalIdx}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  overflow: "hidden",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark" ? "grey.800" : "grey.100",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Chip
                      label={`#${globalIdx}`}
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}
                    >
                      Front
                    </Typography>
                  </Stack>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      lineHeight: 1.4,
                    }}
                  >
                    {card?.front || "—"}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ p: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      mb: 1,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    Back
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      lineHeight: 1.6,
                    }}
                  >
                    {card?.back || "—"}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
      <PaginationFooter
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        total={cards.length}
        label={`${start}–${end}`}
      />
    </Box>
  );
}

export default function CopilotUsageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("summary");
  const [perPage, setPerPage] = useState(10);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await GetAdminCopilotMaterialCall(id);
    setData(res);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const material = data?.material || null;
  const studyPack = data?.studyPack || null;
  const ready = !!data?.ready;

  /*
    Drop the "images" tab for audio materials, same as the mobile app —
    transcribed audio doesn't have figures to extract, so a perpetually-
    empty tab adds noise and confuses the admin.
  */
  const visibleTabs = useMemo(() => {
    const base = [
      { key: "summary", label: "Summary" },
      { key: "structures", label: "Key Points" },
      { key: "quiz", label: "Quiz" },
      { key: "flashcards", label: "Flashcards" },
    ];
    if (material?.sourceType !== "audio") {
      base.splice(2, 0, { key: "images", label: "Images" });
    }
    return base;
  }, [material?.sourceType]);

  useEffect(() => {
    if (!visibleTabs.find((t) => t.key === activeTab)) {
      setActiveTab(visibleTabs[0]?.key || "summary");
    }
  }, [visibleTabs, activeTab]);

  const renderActiveTab = () => {
    if (!ready || !studyPack) {
      const stateLabel =
        material?.status === "failed"
          ? material?.errorMessage ||
            "Processing failed for this material."
          : "Study pack is still being generated. Try refreshing in a moment.";
      return (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            {stateLabel}
          </Typography>
        </Box>
      );
    }
    switch (activeTab) {
      case "summary":
        return <SummaryTab studyPack={studyPack} />;
      case "structures":
        return <StructuresTab studyPack={studyPack} perPage={perPage} />;
      case "images":
        return <ImagesTab studyPack={studyPack} perPage={perPage} />;
      case "quiz":
        return <QuizTab studyPack={studyPack} perPage={perPage} />;
      case "flashcards":
        return <FlashcardsTab studyPack={studyPack} perPage={perPage} />;
      default:
        return null;
    }
  };

  const fullName = material?.user
    ? material.user.fullName ||
      [material.user.firstName, material.user.lastName]
        .filter(Boolean)
        .join(" ") ||
      "Unknown user"
    : "Unknown user";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        component="main"
        sx={{
          py: { xs: 3, md: 4 },
          px: { xs: 2, sm: 3, md: 4 },
          maxWidth: 1400,
          mx: "auto",
        }}
      >
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/copilot-usage")}
              size="small"
              sx={{ color: "text.primary" }}
            >
              Back to Copilot usage
            </Button>
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Refresh">
              <IconButton
                onClick={load}
                size="small"
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={32} />
              </Box>
            ) : !material ? (
              <Typography color="text.secondary">
                This material could not be found. It may have been deleted.
              </Typography>
            ) : (
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  alignItems={{ xs: "flex-start", md: "center" }}
                  justifyContent="space-between"
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ minWidth: 0, flex: 1 }}
                  >
                    <Avatar
                      src={material.user?.profileImage || undefined}
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: "primary.main",
                        fontSize: 16,
                      }}
                    >
                      {initialsFor(material.user)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <MDTypography
                        variant="h5"
                        fontWeight="bold"
                        sx={{
                          wordBreak: "break-word",
                          lineHeight: 1.3,
                        }}
                      >
                        {material.title || "Untitled material"}
                      </MDTypography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.25 }}
                      >
                        Uploaded by{" "}
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          {fullName}
                        </Box>
                        {material.user?.email
                          ? ` · ${material.user.email}`
                          : ""}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <Chip
                      label={
                        STATUS_LABEL[material.status] || material.status || "—"
                      }
                      color={statusChipColor(material.status)}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip
                      label={
                        SOURCE_TYPE_LABEL[material.sourceType] ||
                        material.sourceType ||
                        "—"
                      }
                      variant="outlined"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                    {material.fileUrl ? (
                      <Tooltip title="Open original file">
                        <IconButton
                          size="small"
                          component="a"
                          href={material.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                          }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Stack>
                </Stack>

                <Divider />

                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}
                    >
                      Uploaded
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatDateTime(material.createdAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}
                    >
                      Last update
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatDateTime(material.updatedAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}
                    >
                      File size
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatBytes(material.fileSizeBytes)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}
                    >
                      Credits used
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {material.creditsCost == null
                        ? "—"
                        : material.creditsCost}
                    </Typography>
                  </Grid>
                  {material.fileName ? (
                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                        }}
                      >
                        File name
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{ wordBreak: "break-all" }}
                      >
                        {material.fileName}
                      </Typography>
                    </Grid>
                  ) : null}
                </Grid>
              </Stack>
            )}
          </Paper>

          {!loading && material ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", md: "center" }}
                justifyContent="space-between"
                sx={{
                  px: 2,
                  pt: 1.5,
                  pb: 0,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={(_e, v) => setActiveTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    minHeight: 44,
                    "& .MuiTab-root": {
                      minHeight: 44,
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: "0.875rem",
                    },
                  }}
                >
                  {visibleTabs.map((tab) => (
                    <Tab key={tab.key} value={tab.key} label={tab.label} />
                  ))}
                </Tabs>
                {/* Per-page selector applies to every paginated tab body. */}
                {activeTab !== "summary" ? (
                  <FormControl size="small" sx={{ minWidth: 130, pb: 1 }}>
                    <InputLabel id="copilot-per-page">Per page</InputLabel>
                    <Select
                      labelId="copilot-per-page"
                      value={perPage}
                      label="Per page"
                      onChange={(e) =>
                        setPerPage(parseInt(e.target.value, 10) || 10)
                      }
                      sx={{ height: 36 }}
                    >
                      {PER_PAGE_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt} per page
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : null}
              </Stack>

              <Box sx={{ p: { xs: 2, md: 3 } }}>{renderActiveTab()}</Box>
            </Paper>
          ) : null}
        </Stack>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
